import React, { createContext, useContext, useState, ReactNode } from 'react';

// Define time range types
export type TimeRangeType = '24H' | '7D' | '14D' | '30D' | '90D' | '6M' | '1Y' | 'POP' | 'Current compl.' | null;

// Define the context interface
interface TimeRangeContextType {
  selectedRange: TimeRangeType;
  setSelectedRange: (range: TimeRangeType) => void;
  dateRangeText: string;
}

// Create the context with default values
const TimeRangeContext = createContext<TimeRangeContextType>({
  selectedRange: '7D',
  setSelectedRange: () => {
    console.log('No provider');
  },
  dateRangeText: 'Last 7 days: 10-19-2023 - 10-26-2023',
});

// Custom hook to use the context
export const useTimeRange = () => useContext(TimeRangeContext);

// Helper function to generate date range text based on selected range
const generateDateRangeText = (range: TimeRangeType): string => {
  const today = new Date();
  let startDate = new Date(today);
  let endDate = new Date(today);
  let prefix = 'Last';

  switch (range) {
    case '24H':
      startDate.setDate(today.getDate() - 1);
      prefix = 'Last';
      break;
    case '7D':
      startDate.setDate(today.getDate() - 7);
      prefix = 'Last 7 days';
      break;
    case '14D':
      startDate.setDate(today.getDate() - 14);
      prefix = 'Last 14 days';
      break;
    case '30D':
      startDate.setDate(today.getDate() - 30);
      prefix = 'Last 30 days';
      break;
    case '90D':
      startDate.setDate(today.getDate() - 90);
      prefix = 'Last 90 days';
      break;
    case '6M':
      startDate.setMonth(today.getMonth() - 6);
      prefix = 'Last 6 months';
      break;
    case '1Y':
      startDate.setFullYear(today.getFullYear() - 1);
      prefix = 'Last year';
      break;
    case 'POP':
      // Period over Period - using previous month as an example
      startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      // Create a new date for the end of previous month
      endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      prefix = 'Previous month';
      break;
    case 'Current compl.':
      // Current complete month
      startDate = new Date(today.getFullYear(), today.getMonth(), 1);
      prefix = 'Current month';
      break;
    default:
      startDate.setDate(today.getDate() - 7);
      prefix = 'Last 7 days';
  }

  // Format dates as MM-DD-YYYY
  const formatDate = (date: Date): string => {
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  };

  return `${prefix}: ${formatDate(startDate)} - ${formatDate(endDate)}`;
};

// Provider component
interface TimeRangeProviderProps {
  children: ReactNode;
}

export const TimeRangeProvider: React.FC<TimeRangeProviderProps> = ({ children }) => {
  const [selectedRange, setSelectedRange] = useState<TimeRangeType>(null);
  const [dateRangeText, setDateRangeText] = useState<string>(generateDateRangeText('7D'));

  // Update date range text when selected range changes
  const handleRangeChange = (range: TimeRangeType) => {
    setSelectedRange(range);
    setDateRangeText(generateDateRangeText(range));
  };

  return (
    <TimeRangeContext.Provider
      value={{
        selectedRange,
        setSelectedRange: handleRangeChange,
        dateRangeText,
      }}
    >
      {children}
    </TimeRangeContext.Provider>
  );
};
